import apiClient from "./apiClient";
export type AuctionStatus = "ACTIVE" | "RECONCILING" | "CLOSED";
export type WinStatus =
  "PENDING" | "PAID" | "PAYMENT_FAILED" | "CANCELLED" | "REFUNDED";
export interface AuctionTotals {
  totalWins: number;
  paidWins: number;
  uniqueBuyers: number;
  grossRevenueCents: number;
  cogsCents: number;
  platformFeesCents: number;
  packagingCostCents: number;
  shippingCostCents: number;
  shippingRevenueCents: number;
  netProfitCents: number;
  marginPercent: number;
  pendingAmountCents: number;
}
export interface AuctionSession {
  _id: string;
  name: string;
  platform: "TIKTOK";
  startedAt: string;
  endedAt?: string;
  status: AuctionStatus;
  platformFeeBasisPoints: number;
  historicalImportDefault?: boolean;
  notes?: string;
  totals?: AuctionTotals;
}
export interface AuctionBuyer {
  _id: string;
  username: string;
  normalizedUsername: string;
  displayName?: string;
  notes?: string;
  totals?: AuctionTotals;
  totalItemsPurchased?: number;
  auctionSessionsPurchasedFrom?: number;
  mostRecentPurchaseDate?: string;
}
export interface AuctionWin {
  _id: string;
  auctionSessionId: string;
  auctionBuyerId: string;
  usernameSnapshot: string;
  normalizedUsername: string;
  inventoryItemId: string;
  inventoryItemNameSnapshot: string;
  costPerUnitCents: number;
  brandSnapshot?: string;
  quantity: number;
  winningPriceCents: number;
  platformFeeCents: number;
  saleDate: string;
  sequence: number;
  tiktokOrderId?: string;
  status: WinStatus;
  source: string;
  historicalImport: boolean;
  inventoryAdjustmentApplied: boolean;
  costSource?: "INVENTORY" | "OVERRIDE";
  notes?: string;
}
export interface AuctionPackage {
  _id: string;
  auctionSessionId: string;
  auctionBuyerId: string;
  auctionWinIds: string[];
  boxSizeLabel?: string;
  packagingCostCents: number;
  actualShippingCostCents: number;
  customerPaidShippingCents: number;
  platformShippingCreditCents: number;
  tiktokOrderId?: string;
  trackingNumber?: string;
  status: string;
  notes?: string;
}
export interface AuctionBoxPreset {
  _id: string;
  label: string;
  packagingCostCents: number;
}
export interface SessionDetail {
  session: AuctionSession;
  wins: AuctionWin[];
  packages: AuctionPackage[];
  totals: AuctionTotals;
}
export interface AuctionBuyerDetail {
  buyer: AuctionBuyer;
  wins: AuctionWin[];
  packages: AuctionPackage[];
  totals: AuctionTotals;
}
export interface AuctionFinanceResponse {
  source: "LIVE_AUCTION";
  totals: AuctionTotals;
  wins: AuctionWin[];
}
export const liveAuctionsApi = {
  sessions: async () =>
    (await apiClient.get<AuctionSession[]>("/live-auctions/sessions")).data,
  createSession: async (
    body: Partial<AuctionSession> & { platformFeeRate?: number },
  ) =>
    (await apiClient.post<AuctionSession>("/live-auctions/sessions", body))
      .data,
  session: async (id: string) =>
    (await apiClient.get<SessionDetail>(`/live-auctions/sessions/${id}`)).data,
  updateSession: async (id: string, body: Partial<AuctionSession>) =>
    (
      await apiClient.patch<AuctionSession>(
        `/live-auctions/sessions/${id}`,
        body,
      )
    ).data,
  buyers: async (q = "") =>
    (
      await apiClient.get<AuctionBuyer[]>("/live-auctions/buyers", {
        params: { q },
      })
    ).data,
  buyer: async (id: string) =>
    (await apiClient.get<AuctionBuyerDetail>(`/live-auctions/buyers/${id}`))
      .data,
  boxPresets: async () =>
    (await apiClient.get<AuctionBoxPreset[]>("/live-auctions/box-presets"))
      .data,
  createBoxPreset: async (body: {
    label: string;
    packagingCost?: number;
    packagingCostCents?: number;
  }) =>
    (await apiClient.post<AuctionBoxPreset>("/live-auctions/box-presets", body))
      .data,
  updateBoxPreset: async (
    id: string,
    body: {
      label?: string;
      packagingCost?: number;
      packagingCostCents?: number;
    },
  ) =>
    (
      await apiClient.patch<AuctionBoxPreset>(
        `/live-auctions/box-presets/${id}`,
        body,
      )
    ).data,
  deleteBoxPreset: async (id: string) =>
    apiClient.delete(`/live-auctions/box-presets/${id}`),
  createBuyer: async (body: {
    username: string;
    displayName?: string;
    notes?: string;
  }) =>
    (await apiClient.post<AuctionBuyer>("/live-auctions/buyers", body)).data,
  createWin: async (body: Record<string, unknown>) =>
    (await apiClient.post<AuctionWin>("/live-auctions/wins", body)).data,
  updateWin: async (id: string, body: Record<string, unknown>) =>
    (await apiClient.patch<AuctionWin>(`/live-auctions/wins/${id}`, body)).data,
  deleteWin: async (id: string) =>
    apiClient.delete(`/live-auctions/wins/${id}`),
  applyInventory: async (id: string) =>
    (
      await apiClient.post<AuctionWin>(
        `/live-auctions/wins/${id}/apply-inventory`,
      )
    ).data,
  restoreInventory: async (id: string) =>
    (
      await apiClient.post<AuctionWin>(
        `/live-auctions/wins/${id}/restore-inventory`,
      )
    ).data,
  createPackage: async (body: Record<string, unknown>) =>
    (await apiClient.post<AuctionPackage>("/live-auctions/packages", body))
      .data,
  updatePackage: async (id: string, body: Record<string, unknown>) =>
    (
      await apiClient.patch<AuctionPackage>(
        `/live-auctions/packages/${id}`,
        body,
      )
    ).data,
  finance: async (params: {
    start?: string;
    end?: string;
    sessionId?: string;
    buyerId?: string;
    inventoryItemId?: string;
  }) =>
    (
      await apiClient.get<AuctionFinanceResponse>("/live-auctions/finance", {
        params,
      })
    ).data,
};
